import path from 'path';
import MagicString from 'magic-string';
import { parse as parseSFC } from '@vue/compiler-sfc';
import { parse as parseTemplate, NodeTypes, ElementNode } from '@vue/compiler-dom';
import { TTransformer } from './type';
import { EDITABLE_ELEMENT, HTML_TAGS } from './constants';
import { findWorkspaceRoot } from './utils';

// Helper function to check if current element is a child of SVG
const isSvgChild = (node: ElementNode, parentStack: ElementNode[] = []): boolean => {
  // 如果当前元素是svg本身，返回false
  if (node?.tag?.toLowerCase() === 'svg') return false;

  // 检查父节点栈中是否有svg元素
  return parentStack.some((parent) => parent?.tag?.toLowerCase() === 'svg');
};

// --- Vue 处理 ---
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformVue: TTransformer = (code: string, id: string, prefix: string, opts?: { attributes?: any; filterTag?: any }) => {
  // 向上查找，直到找到名为 workspace 的目录
  const projectRoot = findWorkspaceRoot(process.cwd());
  const relativePath = path.relative(projectRoot, id);

  const { descriptor } = parseSFC(code, {
    filename: id,
    sourceMap: true,
  });
  const { template } = descriptor;
  if (!template) return { code, map: null };

  const startOffset = template.loc.start.offset;
  const ms = new MagicString(code);

  const ast = parseTemplate(template.content, {
    comments: false,
    isNativeTag: (tag) => true,
  });

  const injectAttributes = (node: ElementNode, parentStack: ElementNode[] = []) => {
    if (node.type === NodeTypes.ELEMENT) {
      // 跳过已经注入过属性的元素
      const hasLocatorAttr = node.props.some(
        (prop) => prop.type === NodeTypes.ATTRIBUTE && (prop.name === `data-${prefix}-path` || prop.name === `data-${prefix}-line`)
      );
      if (hasLocatorAttr) {
        return;
      }

      // 跳过 <template> 标签
      if (node.tag === 'template') return;

      // 检查是否是SVG的子节点
      if (isSvgChild(node, parentStack)) return;

      // 过滤标签
      let shouldInject = true;
      if (opts?.filterTag) {
        if (Array.isArray(opts.filterTag)) {
          shouldInject = opts.filterTag.includes(node.tag);
        } else if (typeof opts.filterTag === 'function') {
          shouldInject = opts.filterTag(node.tag);
        }
      } else {
        shouldInject = HTML_TAGS.includes(node.tag);
      }
      if (!shouldInject) return;

      const insertOffset = startOffset + node.loc.start.offset + node.tag.length + 1;
      const line = template.loc.start.line + node.loc.start.line - 1;

      const StartColumn = node.loc.start.column - 1;

      let attrs: string[] = [];
      if (opts?.attributes) {
        const customAttrs = opts.attributes({ relativePath, line, column: StartColumn, tag: node.tag });
        attrs = Object.entries(customAttrs).map(([k, v]) => ` ${k}="${v}" `);
      } else {
        // 获取静态文本内容
        const isTextElement = EDITABLE_ELEMENT.includes(node?.tag?.toLowerCase());
        let staticText = '';

        if (isTextElement && node.children) {
          const hasDynamicContent = node.children.some(
            (child) => child.type === NodeTypes.INTERPOLATION || child.type === NodeTypes.COMPOUND_EXPRESSION
          );

          if (!hasDynamicContent) {
            staticText = node.children
              .filter((child) => child.type === NodeTypes.TEXT)
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((child) => (child as any).content)
              .join('');
          }
        }

        attrs = [
          ` data-${prefix}-path="${relativePath}" `,
          ` data-${prefix}-line="${line}" `,
          ` data-${prefix}-start-column="${StartColumn}" `,
          ` data-${prefix}-tag="${node.tag}" `,
          ` data-${prefix}-component="${encodeURIComponent(template.content.split('\n')[line - 1]?.trim() || '')}" `,
          ` data-${prefix}-id="${relativePath}:${line}:${StartColumn}" `,
          ` data-${prefix}-text="${encodeURIComponent(staticText)}" `,
        ];
      }

      if (HTML_TAGS.includes(node.tag)) {
        ms.appendLeft(insertOffset, attrs.join(' '));
      }
    }

    if (node.children) {
      node.children.forEach((child) => {
        if (child.type === NodeTypes.ELEMENT) {
          injectAttributes(child, [...parentStack, node]);
        }
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  injectAttributes(ast as any);

  return {
    code: ms.toString(),
    map: ms.generateMap({ hires: true }),
  };
};
